import { HighGuideRecommendationType } from './high-guide-recommendation-type';
import { HighGuideRecommendationTag } from './high-guide-recommendation-tag';
import { Deserializable } from '../../../protocols/deserializable';
import { ProductSignalUser } from './product-signal-user';

/**
 * A single budtender's signal for a variant.
 *
 * Field-for-field port of the model in the HighGuide app. The dashboard fetches these purely to read - creating and
 * updating signals happens inside HighGuide itself - so the write helpers (`onSerialize`, `toggleFavorite`,
 * `clearRecommendationFields`) are deliberately not carried over. Adding them here would advertise a capability the
 * dashboard does not have.
 *
 * The two apps must keep the field list in sync by hand. There is no shared package, and nothing here fails at
 * compile time if HighGuide adds a field - it will simply be dropped on the way through the iframe relay.
 */
export class HighGuideProductSignal implements Deserializable {
  /**
   * Client-only mirror of `variantId`, not a server field. The OPFS json writer matches elements inside a cached
   * array by `item.id`, so this is what makes granular array cache transactions possible.
   *
   * Note this is only unique for a single user's signals. Company-wide signals contain one row per user per
   * variant, so `id` collides there and must not be used as a key.
   */
  id: string;

  highGuideUserId: string;
  variantId: string;

  /**
   * Stamped server side - `companyId` from the authenticated company, `locationId` from the budtender's active
   * location at the time the signal was written. Company-wide signals come back with `locationId: 0`.
   */
  companyId: number;
  locationId: number;

  isFavorite: boolean;
  note: string;
  recommendationType: HighGuideRecommendationType;
  recommendationTags: HighGuideRecommendationTag[];
  recommendationScore: number;

  /**
   * Barcode for Canada, otherwise excluded.
   */
  bpdId: string;

  /**
   * Structured boost items (normalized score out of 10).
   */
  overallFeel: number;
  dankness: number;
  valueForMoney: number;

  /**
   * Boost score from note sentiment. Value from 0-2.
   */
  sentimentBoostScore: number;

  /**
   * Final computed signal score from all criteria, clamped to the range -1..10.
   */
  productSignalScore: number;

  productSignalScoreDate: number;
  dateCreated: number;
  lastModified: number;

  onDeserialize(): void {
    this.recommendationTags = this.recommendationTags || [];
    this.id = this.variantId;
  }

  hasNote(): boolean {
    return (this.note?.trim()?.length ?? 0) > 0;
  }

  /**
   * Whether two rows are the same signal - one author's single signal for one variant.
   *
   * The author's id is the identity for a named budtender, since the backend holds one signal per user per variant.
   * Anonymous authors all arrive under the shared `anonymous` sentinel, so several genuinely different reviewers
   * land on that one id and it cannot identify a row by itself; `productSignalScoreDate` breaks the tie there. Two
   * anonymous reviews of the same variant in the same second is not something the product can produce.
   *
   * `productSignalScoreDate` rather than `dateCreated` because it is the timestamp both apps model, which keeps
   * this identical to the copy HighGuide dedupes with.
   *
   * Used to fold websocket-delivered signals into the pool without duplicating what is already held.
   */
  isSameSignalAs(other: HighGuideProductSignal): boolean {
    if (!other) return false;
    if (this.variantId !== other.variantId) return false;
    if (this.highGuideUserId !== other.highGuideUserId) return false;
    if (this.highGuideUserId === ProductSignalUser.anonymousUserId) {
      return this.productSignalScoreDate === other.productSignalScoreDate;
    }
    return true;
  }
}
